<script setup lang="ts">
import type { EventVault } from "@/models"
import { computed, ref, watch } from "vue"
import { useGenerateZip } from "@/api"
import { DrawerContextCard, EventExtendCard, ZipStateSection } from "@/components"
import { Alert, Button, Checkbox, Drawer, NoData } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { EventVaultStatus } from "@/models"
import { EventDeleteDrawerKey } from "@/symbols"

interface EventDeleteDrawerProps {
  eventVault?: EventVault
  isLoading?: boolean
}

const props = withDefaults(defineProps<EventDeleteDrawerProps>(), { isLoading: false })

const emit = defineEmits<{
  (e: "deleteEvent", eventVaultId?: number): void
  (e: "zipRequested"): void
}>()

const confirmDownload = ref(false)

const { t } = useTranslation()
const toast = useToast()

const { isActive, toggle } = useModal(EventDeleteDrawerKey)
const { mutate: mutateGenerateZip, isPending } = useGenerateZip()

const canDeleteEvent = computed(() => {
  return props.eventVault?.status === EventVaultStatus.Expiring
    || props.eventVault?.status === EventVaultStatus.Expired
})

const drawerTitle = computed(() => {
  return props.eventVault?.status === EventVaultStatus.Expiring
    ? t("event_delete_drawer.title_expiring")
    : t("event_delete_drawer.title")
})

const showExtendUpsell = computed(() => {
  return canDeleteEvent.value && !props.eventVault?.freemiumMode
})

const hasZipDownloadUrl = computed(() => Boolean(props.eventVault?.zipSummary?.downloadUrl))
const isGeneratingZip = computed(() => isPending.value)

function onGenerateZip() {
  if (!props.eventVault?.id) {
    toast.error()
    return
  }

  mutateGenerateZip({ eventVaultId: props.eventVault.id, options: {} }, {
    onSuccess: () => emit("zipRequested"),
    onError: () => toast.error(),
  })
}

watch(
  () => [isActive.value, props.isLoading, props.eventVault?.status] as const,
  ([isOpen, isLoading]) => {
    if (!isOpen || isLoading || canDeleteEvent.value)
      return

    toggle()
  },
  { immediate: true },
)
</script>

<template>
  <Drawer :name="EventDeleteDrawerKey" :title="t('event_delete_drawer.heading')">
    <div v-if="isLoading" class="flex flex-col gap-5">
      <div class="gpx-skeleton w-full h-24" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-1/3 h-12" />
    </div>
    <div v-else-if="eventVault">
      <div>
        <DrawerContextCard :event-vault="eventVault" />
        <div class="flex flex-col gap-2 font-montserrat">
          <h4 class="font-semibold">
            {{ drawerTitle }}
          </h4>
          <Alert :closable="false" prefix-icon="CircleInfo" class="text-xs mb-4">
            {{ t("event_delete_drawer.alert") }}
          </Alert>

          <p>{{ t("event_delete_drawer.download_message") }}</p>

          <ZipStateSection
            :zip-summary="eventVault?.zipSummary"
            :is-generating="isGeneratingZip"
            mode="delete"
          >
            <template #default="{ zipState }">
              <Button
                v-if="zipState === 'ready' && eventVault?.zipSummary?.downloadUrl"
                :href="eventVault.zipSummary.downloadUrl"
                class="w-fit mt-2 mb-6"
              >
                {{ t("event_delete_drawer.download_button") }}
              </Button>

              <Button
                v-else-if="zipState === 'missing'"
                class="w-fit"
                :disabled="isGeneratingZip"
                data-test="generate_zip_button"
                @click="onGenerateZip"
              >
                {{ t("zip_export_drawer.generate") }}
              </Button>
            </template>
          </ZipStateSection>

          <div v-if="showExtendUpsell" class="flex flex-col gap-2">
            <h4 class="font-semibold">
              {{ t("event_delete_drawer.keep_data_question") }}
            </h4>

            <EventExtendCard :event-vault="eventVault" />
          </div>
        </div>
      </div>
    </div>
    <NoData v-else :button-label="t('close')" @click="toggle" />

    <template #footer>
      <div class="px-6 py-2 md:py-4 bg-white">
        <Checkbox
          v-if="hasZipDownloadUrl" v-model="confirmDownload" name="confirm"
          :label="t('event_delete_drawer.confirm_download')"
        />

        <Button
          variant="danger" :disabled="!confirmDownload || !hasZipDownloadUrl || !canDeleteEvent"
          class="w-full mt-4" @click="() => emit('deleteEvent', eventVault?.id)"
        >
          {{ t("event_delete_drawer.delete_button") }}
        </Button>
      </div>
    </template>
  </Drawer>
</template>
